import { type Expect } from '@type-challenges/utils';
import { UnreachableCaseError } from 'ts-essentials';
import { type PickDeep, type SimplifyDeep, type ValueOf } from 'type-fest';
import {
  type RootState,
  type StrictSubsetOfRootState,
} from '../core/root.state';
import { PrayerStreamUserStatus } from '../core/stream/stream.slice';

export const RouteAccess = {
  EVERYONE: 'Everyone',
  GUEST: 'Guest',
  LOGGED_IN_USER: 'LoggedInUser',
  REGISTERED_USER: 'RegisteredUser',
} as const;

export type RouteAccess = ValueOf<typeof RouteAccess>;

export type VerifyRouteAccessState = SimplifyDeep<
  PickDeep<RootState, 'stream.prayerStreamUserStatus'>
>;

type CheckState = Expect<StrictSubsetOfRootState<VerifyRouteAccessState>>;

const checkAccess = (
  access: RouteAccess,
  loggedIn: boolean,
  streamState: VerifyRouteAccessState['stream'],
): boolean => {
  const { prayerStreamUserStatus } = streamState;
  // Check if the user is logged out, and if so, return false for all
  // permissions except for `EVERYONE` and `GUEST`. If for any reason checks
  // such as `isActiveMSCompleteMember` are set to `true`, but the user is
  // logged out, we should not return `true`.
  if (
    !loggedIn &&
    access !== RouteAccess.EVERYONE &&
    access !== RouteAccess.GUEST
  ) {
    return false;
  }

  switch (access) {
    case RouteAccess.EVERYONE:
      return true;
    case RouteAccess.GUEST:
      return !loggedIn;
    case RouteAccess.LOGGED_IN_USER:
      return loggedIn;
    case RouteAccess.REGISTERED_USER:
      return prayerStreamUserStatus === PrayerStreamUserStatus.REGISTERED;
    default:
      throw new UnreachableCaseError(access);
  }
};

type VerifyRouteAccessOptions = {
  excludeList?: RouteAccess[];
};

export const verifyRouteAccess = (
  accessList: RouteAccess[],
  loggedIn: boolean,
  streamState: VerifyRouteAccessState['stream'],
  options?: VerifyRouteAccessOptions,
): boolean => {
  const { excludeList } = options ?? {};

  if (
    excludeList?.some((access) => checkAccess(access, loggedIn, streamState))
  ) {
    return false;
  }

  const allowed = accessList.find((access) =>
    checkAccess(access, loggedIn, streamState),
  );
  return !!allowed;
};

export const getLoginRequired = (accessList: RouteAccess[]): boolean => {
  return !(
    accessList.includes(RouteAccess.GUEST) ||
    accessList.includes(RouteAccess.EVERYONE)
  );
};
